import { useEffect, useState } from "react";
import { Wifi, WifiOff, Loader2 } from "lucide-react";
import { subscribeQueue, flushQueue } from "@/lib/syncQueue";

/** Compact badge shown in the header (mobile + desktop). */
export default function OfflineIndicator() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const unsubscribe = subscribeQueue(({ pending: p }) => setPending(p));
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      unsubscribe();
    };
  }, []);

  if (online && pending === 0) {
    return null; // Hidden when everything is fine
  }

  const handleSync = async () => {
    if (!online || syncing) return;
    setSyncing(true);
    try {
      await flushQueue();
    } finally {
      setSyncing(false);
    }
  };

  if (!online) {
    return (
      <div
        data-testid="offline-indicator"
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-medium"
      >
        <WifiOff className="w-3.5 h-3.5" />
        Hors ligne{pending > 0 && ` · ${pending} en attente`}
      </div>
    );
  }

  // Online + pending > 0
  return (
    <button
      data-testid="sync-indicator"
      type="button"
      onClick={handleSync}
      disabled={syncing}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-[var(--vidoc-primary)] text-xs font-medium hover:bg-blue-100 touch-target"
      title="Synchroniser maintenant"
    >
      {syncing ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Wifi className="w-3.5 h-3.5" />
      )}
      {pending} en attente
    </button>
  );
}
